/**
 * Generic function to fetch all items from a paginated API
 * @param fetchFunction - The API function to call (e.g., fetchOrders, fetchPayments)
 * @param params - The parameters to pass to the fetch function
 * @param responseKey - The key in the response object that contains the items array (e.g., 'orders', 'payments')
 * @param batchSize - The number of items to fetch per request (default: LAYUP_API_GET_ITEMS_LIMIT)
 * @returns Promise<T[]> - Array of all items
 */
export const fetchAllPaginated = async <T, P extends { skip?: number; limit?: number }>(
  fetchFunction: (params: P) => Promise<{ [key: string]: any }>,
  params: P,
  responseKey: string,
  batchSize: number = 500,
): Promise<T[]> => {
  const items: T[] = [];
  let skip = 0;
  let hasMore = true;

  while (hasMore) {
    const requestParams = {
      ...params,
      skip,
      limit: batchSize,
    } as P;

    const response = await fetchFunction(requestParams);
    const fetchedItems = response[responseKey] as T[] | undefined;

    if (fetchedItems && fetchedItems.length > 0) {
      items.push(...fetchedItems);
      skip += fetchedItems.length;

      // If we received fewer items than requested, we've reached the end
      if (fetchedItems.length < batchSize) {
        hasMore = false;
      }
    } else {
      hasMore = false;
    }
  }

  return items;
};

/**
 * Generate a CSV blob URL from data array and header mapping
 * @param headerMap - Object mapping data keys to CSV column headers
 * @param data - Array of data objects to export
 * @returns string - Blob URL for the generated CSV file
 */
export const generateCsvBlobUrl = <T extends Record<string, any>>(
  headerMap: Partial<Record<keyof T, string>>,
  data: T[],
): string => {
  const headers = Object.keys(headerMap) as Array<keyof T>;
  const headerTitles = headers.map((header) => headerMap[header] || header);
  const rows = data.map((row: T) =>
    headers.map((header) => `"${String(row[header] ?? '').replace(/"/g, '""')}"`).join(','),
  );

  const csvContent = [headerTitles.join(','), ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  return url;
};
